import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { parse } from 'date-fns';
import {
  fullNameToDrLastName,
  fullNameToDrLastNameMiddleEast,
  fullNameToLastNameRest,
  isDimmed,
  NormalizedStatus,
  normalizeStatus,
} from '../shared/procedureUtils';
import has from 'lodash/has';
import { translateToTz } from '@/util/dateTime';
import { useScope } from '@/hooks/useScope';
import { Procedure as ProcedureT } from '@/types/Procedure';
import isMiddleEast from '@/util/isMiddleEast';
import { isPreOpAsDPUOptimized } from '../../tablet/utils';
import { Box } from '@material-ui/core';
import { getInOrStatusLabel } from '../statusUtils';
import { alpha, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import CardTiny from '../timeline/components/CardTiny';
import { getDisposition } from '../timeline/Procedure';
import CardHeader from './CardHeader';
import CardSubheader from './CardSubheader';
import CardNote from '../shared/CardNote';
import CardProcedureType from './CardProcedureType';
import { getRelativeDuration } from './utils';
import { useStatusDisplay } from '@/hooks/useStatusColor';

const Header = styled.div<any>`
  display: flex;
  background-color: rgba(255, 255, 255, 0.05);
  box-sizing: border-box;
  font-size: 1em;
  flex-direction: column;
  position: relative;
  z-index: 1;

  ${props =>
    props.isInactive
      ? css`
          opacity: 0.3;
        `
      : null}

  ${props =>
    props.isDisabled
      ? css`
          opacity: 0.3;
          pointer-events: none;
          border: none;
          animation: none;
        `
      : null}

  ${props =>
    props.stretch
      ? css`
          height: 100%;
        `
      : null}

  ${props =>
    props.row
      ? css`
          flex-direction: row;
        `
      : null}

  ${props =>
    props.column
      ? css`
          flex-direction: column;
          height: 100%;

          > * {
            flex: 1 0 auto;
            padding-top: 0;
            padding-bottom: 0;
          }
        `
      : null}
`;

const FULL_HEIGHT_DURATION = 140;
const secondInMili = 1000;
const minDuration = 42;

type AnesthesiologistProcedureProps = {
  procedure: ProcedureT;
  dayStart: Date;
  dayEnd: Date;
  onClick: () => void;
  isTouchDevice: boolean;
  showBedNumber: boolean;
  isKiosk?: boolean;
  date: Date;
  inlineText?: boolean;
};

const AnesthesiologistProcedure: FC<AnesthesiologistProcedureProps> = ({
  procedure,
  dayStart,
  dayEnd,
  onClick,
  isTouchDevice,
  showBedNumber,
}) => {
  const scope = useScope();
  const hospitalId = scope?.hospital?.id;
  const { getColor, getContourStyle, getFlashingContourColor } = useStatusDisplay('anesthesiologist');

  const [fullHeight, setFullHeight] = useState(false);

  const handleMouseEnter = () => {
    if (!fullHeight) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setFullHeight(!fullHeight);
      }, secondInMili);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setFullHeight(false);
  };

  const handleOnClick = () => {
    if (isTouchDevice) {
      setFullHeight(!fullHeight);
      setTimeout(() => setFullHeight(false), 10 * secondInMili);
    }
    onClick();
  };

  const middleEast = useMemo(() => isMiddleEast(hospitalId), [hospitalId]);

  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    },
    []
  );

  const startTime = useMemo(
    () => (procedure.startTime ? translateToTz(scope?.hospital?.timezone?.id)(parse(procedure.startTime)) : undefined),
    [procedure.startTime, scope?.hospital?.timezone?.id]
  );
  const duration = !isTouchDevice
    ? procedure.duration || 0
    : !fullHeight
      ? procedure.duration || 0
      : FULL_HEIGHT_DURATION;

  const isShort = (duration || 0) <= 15;

  const topPct = useMemo(() => getDisposition(dayStart, startTime, dayEnd), [dayStart, startTime, dayEnd]);

  const heightPct = useMemo(
    () => getRelativeDuration(dayStart, isShort ? 15 : duration <= 30 ? minDuration : duration, dayEnd),
    [dayStart, dayEnd, duration, isShort]
  );

  const cardStyle = useMemo(
    () => ({
      top: `calc(0.4rem + ${topPct}%)`,
      zIndex: fullHeight ? 5 : 1,
      height: `${heightPct}%`,
    }),
    [topPct, fullHeight, heightPct]
  );

  const normalizedStatus = useMemo(
    () => normalizeStatus(procedure.patient?.status, procedure.patient?.room, middleEast),
    [middleEast, procedure.patient?.room, procedure.patient?.status]
  );

  const status = has(procedure, 'patient.id') ? normalizedStatus : 'PreAdm';
  const inOrStatus = useMemo(
    () => normalizedStatus === NormalizedStatus.In_OR && getInOrStatusLabel(procedure.patient?.status),
    [normalizedStatus, procedure.patient?.status]
  );

  const isInactive = useMemo(() => isDimmed(procedure), [procedure]);
  const isDisabled = useMemo(
    () => [NormalizedStatus.Completed, NormalizedStatus.Canceled].includes(normalizedStatus),
    [normalizedStatus]
  );

  const physicianNameSource = useMemo(
    () => procedure.physician?.name || procedure.anesthesiologist?.name || undefined,
    [procedure.anesthesiologist?.name, procedure.physician?.name]
  );

  const physicianName = useMemo(
    () => (middleEast ? fullNameToDrLastNameMiddleEast(physicianNameSource) : physicianNameSource),
    [middleEast, physicianNameSource]
  );

  useEffect(() => {
    if (!physicianNameSource) {
      console.warn('[AnesthesiologistProcedure] Missing physician/anesthesiologist name for procedure', {
        procedureId: procedure.id,
        startTime: procedure.startTime,
        startTimeText: procedure.startTimeText,
        room: procedure.patient?.room,
        patientId: procedure.patient?.id,
        patientName: procedure.patient?.name,
        physician: procedure.physician,
        anesthesiologist: procedure.anesthesiologist,
      });
    }
  }, [physicianNameSource, procedure]);
  const color = procedure.physician?.color || procedure.physicianColor || '#888888';

  const anesthesiologistName = useMemo(
    () =>
      middleEast
        ? fullNameToDrLastName(procedure.anesthesiologist?.name)
        : fullNameToLastNameRest(procedure.anesthesiologist?.name),
    [middleEast, procedure.anesthesiologist?.name]
  );
  const anesthesiaType = procedure.anesthesiaType;

  const isDPU = useMemo(
    () => isPreOpAsDPUOptimized(procedure.patient?.room?.type, procedure.patient?.events),
    [procedure.patient?.events, procedure.patient?.room?.type]
  );

  const isHelp = useMemo(
    () => Boolean(procedure.patient?.events?.helpActiveAt),
    [procedure.patient?.events?.helpActiveAt]
  );
  const procedureColor = useMemo(
    () => procedure.color || getColor(status, hospitalId, null) || getFlashingContourColor(status),
    [getColor, getFlashingContourColor, hospitalId, procedure.color, status]
  );
  const isFlipRoom = useMemo(
    () => Boolean(procedure.patient?.events?.flipRoomActiveAt),
    [procedure.patient?.events?.flipRoomActiveAt]
  );

  const classes = useStyles({ length });

  const procedureType = useMemo(() => procedure.procedureType?.replaceAll('\n', ' '), [procedure.procedureType]);

  const statusLabel = useMemo(
    () =>
      normalizedStatus === NormalizedStatus.In_OR && getInOrStatusLabel(procedure.patient?.status)
        ? normalizedStatus === NormalizedStatus.In_OR && getInOrStatusLabel(procedure.patient?.status)
        : status,
    [normalizedStatus, procedure.patient?.status, status]
  );

  if ((duration || 0) <= 15) {
    return (
      <Box className={classes.card} style={cardStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Box
          className={clsx(classes.helpContainer, {
            [classes.helpContainerAnimation]: isHelp && !isDisabled,
          })}
        >
          <Box
            className={clsx(classes.wrapper, {
              [classes.disabled]: isDisabled,
              [classes.onClick]: !isInactive && onClick,
              [classes.isDpuFromOr]: isDPU,
              [classes.customBorderColor]: procedureColor,
            })}
            style={
              procedureColor && !isHelp
                ? { borderColor: procedureColor, ...getContourStyle(status, procedureColor) }
                : undefined
            }
            onClick={handleOnClick}
          >
            <Header stretch isInactive isDisabled={isDisabled}>
              <CardTiny
                procedure={procedure}
                physicianName={physicianName}
                anesthesiologistName={anesthesiologistName}
                anesthesiaType={anesthesiaType}
                showBedNumber={showBedNumber}
                color={color}
                status={status}
                hospitalId={hospitalId}
                inOrStatus={inOrStatus}
                isFlipRoom={isFlipRoom}
              />
            </Header>
          </Box>
        </Box>
      </Box>
    );
  } else {
    return (
      <Box className={classes.card} style={cardStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Box
          className={clsx(classes.helpContainer, {
            [classes.helpContainerAnimation]: isHelp && !isInactive && !isDisabled,
            [classes.helpContainerBackground]: fullHeight,
          })}
        >
          <Box
            className={clsx(classes.wrapper, {
              [classes.onClick]: !isInactive && onClick,
              [classes.isDpuFromOr]: isDPU,
              [classes.customBorderColor]: procedureColor,
            })}
            style={
              procedureColor && !isHelp
                ? { borderColor: procedureColor, ...getContourStyle(status, procedureColor) }
                : undefined
            }
            onClick={handleOnClick}
          >
            <Box
              className={clsx(classes.body, {
                [classes.completed]: isInactive,
                [classes.disabled]: isDisabled,
              })}
            >
              <CardHeader
                startTime={procedure.startTimeText}
                endTime={procedure.endTimeText}
                physicianName={physicianName}
              />
              <CardSubheader
                patientInitials={procedure.patientInitials?.replace(' ', '')}
                labelColor={getColor(status, hospitalId)}
                labelText={statusLabel}
                anesthesiaType={anesthesiaType}
                anesthesiologistName={anesthesiologistName}
                hasAnesthesiologistNotes={!!procedure.anesthesiologistNotes}
                hasOrNotes={!!procedure.orNotes}
                isFlipRoom={isFlipRoom}
              />
              {duration <= 45 && <CardProcedureType procedureType={procedureType} />}
              {procedure.anesthesiologistNotes && <CardNote note={procedure.anesthesiologistNotes} />}
              {procedure.orNotes && <CardNote note={procedure.orNotes} />}
              {duration > 45 && <CardProcedureType procedureType={procedureType} />}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
};

export const useStyles = makeStyles(theme => ({
  card: {
    width: '100%',
    position: 'absolute',
    left: 0,
    fontWeight: 500,
    boxSizing: 'border-box',
    fontFamily: '"Roboto", sans-serif',
    letterSpacing: -0.2,
  },
  staffMembersBox: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '0.25em',
    paddingRight: '0.25em',
  },

  header: {
    fontSize: '0.75em',
    paddingRight: '0.25em',
    paddingLeft: '0.25em',
    display: 'flex',
    justifyContent: 'space-between',
  },

  wrapper: {
    height: '100%',
    width: '100%',
    backgroundColor: theme.palette.type === 'light' ? '#fff' : '#222c59',
    borderRadius: '0.16666em',
    boxShadow: `1px 1px 0 ${theme.palette.type === 'light' ? alpha(theme.palette.common.black, 0.1) : '#121d4d'}`,
    overflow: 'hidden',
    border: '2px solid #fff',

    '&:after': {
      content: '',
      display: 'block',
      borderRadius: '0 0 0.16666em 0.16666em',
      position: 'absolute',
      bottom: 0,
      left: '0.5em',
      right: '0.5em',
      height: '0.4em',
    },
  },
  anesthesiologist: {
    fontWeight: 'bold',
    overflow: 'hidden',
    position: 'relative',
  },
  disabled: {
    opacity: 0.3,
    pointerEvents: 'none',
    border: 'none',
    animation: 'none',
  },
  onClick: {
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'transform 100ms ease-in-out, background-color 150ms ease-in-out',

    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.05),
    },

    '&:active': {
      backgroundColor: alpha(theme.palette.primary.dark, 0.075),
    },
  },
  isDpuFromOr: {
    border: '1px solid purple',
  },
  customBorderColor: {
    border: '2px solid transparent',
  },
  noteIndicator: {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5),
    backgroundColor: theme.palette.text.primary,
  },
  completed: {
    opacity: 0.3,

    '&:after': {
      content: '',
      background: 'linear-gradient(0, rgba(34, 44, 87, 1) 0%, rgba(34, 44, 87, 0) 100%)',
    },
  },

  body: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: alpha(theme.palette.background.paper, 0.05),
    fontSize: '1em',
    position: 'relative',
    zIndex: 1,
  },
  stretch: {
    height: '100%',
  },
  row: {
    flexDirection: 'row',
  },
  column: {
    flexDirection: 'column',
    height: '100%',

    '& > *': {
      flex: '1 0 auto',
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  staffMembers: {
    display: 'flex',
    minWidth: 0,
    gap: '0.5em',

    '& > div': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontSize: 'inherit',
      fontFamily: 'inherit',
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  },

  '@keyframes emphasize': {
    '0%': {
      borderColor: 'rgba(255, 0, 0, 0)',
      boxShadow: '0 0 4px rgba(255, 0, 0, 0)',
    },
    '100%': {
      borderColor: 'rgba(255, 0, 0, 1)',
      boxShadow: '0 0 30px rgba(255, 0, 0, 1)',
    },
  },
  helpContainer: {
    border: '3px solid transparent',
    height: '100%',
  },
  helpContainerAnimation: {
    animation: `1.2s $emphasize ease-in alternate infinite`,
  },
  helpContainerBackground: {
    background: theme.palette.background.default,
  },
}));

export default AnesthesiologistProcedure;
